import { GET_BETA_USERS, SEARCH_USERS, UPDATE_USER_GROUP } from '@/gql/betaUsers';
import type {
  GetBetaUsersResponse,
  SearchUsersResponse,
  UpdateUserGroupResponse,
  UpdateUserGroupVariables,
} from '@/gql/betaUsers/types';
import { keys } from '@/gql/global/keys';
import { fetchData } from '@/services/graphql';
import { BetaUser, UserGroup } from '@/types/betaUsers';
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type APIError = Error;

// Transform GraphQL response to frontend format
const transformUser = (user: {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userGroup: string;
  dateJoined: string;
}): BetaUser => ({
  id: user.id,
  firstName: user.firstName,
  lastName: user.lastName,
  email: user.email,
  userGroup: user.userGroup as UserGroup,
  dateJoined: user.dateJoined,
  fullName: `${user.firstName} ${user.lastName}`.trim(),
});

/**
 * Hook to search users by email or name
 */
export const useSearchUsers = (query: string, options?: UseQueryOptions<BetaUser[], APIError>) => {
  return useQuery<BetaUser[], APIError>({
    ...keys.betaUsers.search(query),
    queryFn: async (): Promise<BetaUser[]> => {
      // at least 3 characters to search for users
      if (!query || query.trim().length < 3) {
        return [];
      }

      try {
        // Convert search query to SQL ILIKE pattern
        const searchPattern = `%${query}%`;

        const data = await fetchData<SearchUsersResponse>(SEARCH_USERS, {
          emailSearch: searchPattern,
        });

        return data.user.map(transformUser);
      } catch (error) {
        console.error('Error searching users:', error);
        throw error instanceof Error ? error : new Error('Failed to search users');
      }
    },
    enabled: !!query && query.trim().length >= 3,
    staleTime: 30000, // 30 seconds
    ...options,
  });
};

/**
 * Hook to get all beta users
 */
export const useGetBetaUsers = (options?: UseQueryOptions<BetaUser[], APIError>) => {
  return useQuery<BetaUser[], APIError>({
    ...keys.betaUsers.lists,
    queryFn: async (): Promise<BetaUser[]> => {
      try {
        const data = await fetchData<GetBetaUsersResponse>(GET_BETA_USERS);
        return data.user.map(transformUser);
      } catch (error) {
        console.error('Error fetching beta users:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch beta users');
      }
    },
    ...options,
  });
};

/**
 * Hook to update user group (add or remove beta user)
 */
export const useUpdateUserGroup = (
  options?: UseMutationOptions<BetaUser, APIError, { userId: string; userGroup: UserGroup }>,
) => {
  const queryClient = useQueryClient();

  return useMutation<BetaUser, APIError, { userId: string; userGroup: UserGroup }>({
    mutationFn: async ({ userId, userGroup }): Promise<BetaUser> => {
      try {
        const variables: UpdateUserGroupVariables = {
          userId,
          userGroup,
        };

        const response = await fetchData<UpdateUserGroupResponse>(UPDATE_USER_GROUP, variables);

        return transformUser(response.updateUserByPk);
      } catch (error) {
        console.error('Error updating user group:', error);
        throw error instanceof Error ? error : new Error('Failed to update user group');
      }
    },
    onSuccess: () => {
      // Invalidate beta users list and all search results
      queryClient.invalidateQueries({ ...keys.betaUsers.lists });
      queryClient.invalidateQueries({ ...keys.betaUsers.all });
      // Invalidate all search queries to update search results immediately
      queryClient.invalidateQueries({ queryKey: ['betaUsers', 'search'] });
    },
    ...options,
  });
};

/**
 * Helper function to add a user as beta user
 */
export const useAddBetaUser = (options?: UseMutationOptions<BetaUser, APIError, string>) => {
  const updateUserGroup = useUpdateUserGroup();

  return useMutation<BetaUser, APIError, string>({
    mutationFn: async (userId: string): Promise<BetaUser> => {
      return updateUserGroup.mutateAsync({
        userId,
        userGroup: UserGroup.BETA,
      });
    },
    ...options,
  });
};

/**
 * Helper function to remove a user from beta users
 */
export const useRemoveBetaUser = (options?: UseMutationOptions<BetaUser, APIError, string>) => {
  const updateUserGroup = useUpdateUserGroup();

  return useMutation<BetaUser, APIError, string>({
    mutationFn: async (userId: string): Promise<BetaUser> => {
      return updateUserGroup.mutateAsync({
        userId,
        userGroup: UserGroup.REGULAR,
      });
    },
    ...options,
  });
};
